/**
 * Presentation helpers for the conversation-analysis pages (SA-212).
 * Pure — tested beside the module (house convention, cf.
 * pages/Dashboard/trendSeries.ts).
 */
import { ApiError } from '@/services/apiClient'
import type { AnalysisRunFilters } from '@/types/conversationAnalysis'

export function formatFilterSummary(filters: AnalysisRunFilters): string {
  const parts: string[] = []
  if (filters.start || filters.end) {
    parts.push(`${filters.start ?? '…'} → ${filters.end ?? '…'}`)
  }
  if (filters.channels?.length) parts.push(filters.channels.join(', '))
  if (filters.persona_ids?.length) {
    const n = filters.persona_ids.length
    parts.push(`${n} persona${n === 1 ? '' : 's'}`)
  }
  if (filters.conversation_ids?.length) {
    const n = filters.conversation_ids.length
    parts.push(`${n} conversation${n === 1 ? '' : 's'} by id`)
  }
  if (filters.focus) parts.push('focused')
  return parts.length ? parts.join(' · ') : 'all conversations'
}

/** De-snaked report-type display label ("deep_dive" → "deep dive"); the
 * capitalize CSS class does the casing. */
export function reportTypeLabel(reportType: string): string {
  return reportType.replace(/_/g, ' ')
}

export function formatCost(value: number): string {
  return `$${value.toFixed(4)}`
}

export function displayUserName(
  userId: string | null,
  names: Record<string, string> | undefined,
): string {
  if (!userId) return '—'
  return names?.[userId] ?? (userId.length > 8 ? `${userId.slice(0, 8)}…` : userId)
}

/** Inline-error text: a string ApiError detail is the backend's own message
 * (e.g. the 422 date-range text) and safe to render; anything else falls
 * back to the generic Error message. */
export function analysisErrorMessage(err: unknown): string {
  if (err instanceof ApiError && typeof err.detail === 'string') return err.detail
  return err instanceof Error ? err.message : 'Request failed'
}

/** Filterable channels — shared by RunBuilder and TrendsTab (SA-215). */
export const CHANNEL_OPTIONS = ['web', 'whatsapp', 'widget', 'email', 'voice']

/** Local calendar date — deliberately not toISOString, which shifts the
 * day in negative-UTC-offset browsers (export-control idiom). */
export function toDateInput(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}
